"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { GraduationCap, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import ThemeToggle from "@/components/ThemeToggle";
import { api, type MasterySummary } from "@/lib/api";
import { isPrimaryNavActive, PRIMARY_NAV_ITEMS } from "@/lib/navigation";

export default function Header() {
  const pathname = usePathname();
  const [mastery, setMastery] = useState<MasterySummary | null>(null);
  const [showBadge, setShowBadge] = useState(false);

  // 登录后（路由变化）重新拉取掌握度，避免徽章一直为空
  useEffect(() => {
    if (pathname === "/login") return;
    const refresh = () =>
      api
        .mastery()
        .then((d) => {
          if (d.total_concepts > 0) {
            setMastery(d);
            setShowBadge(true);
          }
        })
        .catch(() => setShowBadge(false));
    refresh();
    window.addEventListener("mastery-changed", refresh);
    return () => window.removeEventListener("mastery-changed", refresh);
  }, [pathname]);

  if (pathname === "/login") return null;

  async function handleLogout() {
    try {
      await api.logout();
    } finally {
      // 登出后整页跳转，清掉所有客户端状态；replace 避免后退回到受保护页
      window.location.replace("/login");
    }
  }

  return (
    <header className="sticky top-0 z-50 border-b bg-card/90 backdrop-blur">
      <div className="mx-auto flex h-[var(--app-header-height)] max-w-6xl items-center justify-between px-4">
        <Link href="/" className="flex items-center gap-2 font-semibold">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <GraduationCap className="h-4 w-4" />
          </span>
          <span className="text-sm sm:text-base">英语语法学习</span>
        </Link>

        <div className="flex items-center gap-1.5">
          <nav aria-label="主导航" className="hidden items-center gap-0.5 sm:flex">
            {PRIMARY_NAV_ITEMS.filter((item) => item.desktop).map((item) => {
              const Icon = item.icon;
              const active = isPrimaryNavActive(pathname, item);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={active ? "page" : undefined}
                  aria-label={
                    showBadge && item.href === "/review" && mastery?.weak
                      ? `${item.label}，${mastery.weak} 个薄弱知识点待复习`
                      : item.label
                  }
                  title={item.label}
                  className={cn(
                    "relative flex h-9 items-center gap-1.5 rounded-md px-3 text-sm transition-colors",
                    active
                      ? "bg-accent font-medium text-accent-foreground"
                      : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden md:inline">{item.label}</span>
                  {showBadge && item.href === "/review" && mastery && mastery.weak > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white">
                      <span aria-hidden="true">{mastery.weak}</span>
                      <span className="sr-only">{mastery.weak} 个薄弱知识点待复习</span>
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
          <ThemeToggle />
          <button
            onClick={handleLogout}
            title="退出登录"
            aria-label="退出登录"
            className="flex h-11 w-11 sm:h-9 sm:w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
